import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Center, Spinner } from 'src/components/atoms';
import {
  AudioRecording,
  handleErrorToast,
  UploadedEvidence,
} from 'src/components/molecules';
import { UploadedEvidenceFile } from 'src/components/molecules/UploadedEvidence/types';
import { PublicLoggedInContainerWithNavbar } from 'src/components/organismsLegacy/LoggedInContainer/PublicLoggedInContainerWithNavbar';
import { CallStep } from 'src/graphql';
import { stripFileExtension } from 'src/utils';
import {
  DateFormats,
  formatDate,
  isValidDate,
  toLocalTimezone,
} from 'src/utils/time';
import { EvidenceType, PUBLIC_REPORT } from '../../../api';
import axios from '../../../axios';
import '../../../css/public/PublicReport.css';
import I18next from '../../../i18n';
import * as Types from '../../../types';
import { getReadableIdWithDash } from '../../../types';
import PublicReportCard from '../PublicReportCard';
import {
  BackLinkContainer,
  BackTextLink,
  Evidence,
  EvidenceCard,
  EvidenceIcon,
  EvidenceList,
  VerticalLine,
} from './PublicReport.styles';

interface Props {
  organisation: string;
  currentUser?: Types.User;
  reportId: string;
  isReporterViewEvidenceDisabled?: boolean;
  isOprCountryEnabled?: boolean;
}

interface State {
  isLoading: boolean;
  publicReport: Types.PublicReport | null;
}

export class PublicReport extends React.Component<Props, State> {
  public constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      publicReport: null,
    };
  }

  public async componentDidMount() {
    this.setState({ isLoading: true });

    try {
      const result = await axios.get(PUBLIC_REPORT(this.props.reportId));
      this.setState({
        publicReport: result.data.publicReport,
      });
    } catch (error) {
      handleErrorToast({ toastMessage: 'Report failed to load.' });
    }
    this.setState({
      isLoading: false,
    });
  }

  private getEvidenceContent = (evidenceType: EvidenceType) => {
    return this.state.publicReport
      ? `${
          this.state.publicReport.evidence.evidence.find(
            (evidence) => evidence.type === evidenceType,
          )?.value
        }`
      : '';
  };

  private getFileUploadEvidenceContent = () => {
    return this.state.publicReport
      ? this.state.publicReport.evidence.evidence
          .filter((evidence) => evidence.type === EvidenceType.PHOTO)
          .map((evidence) => {
            if (typeof evidence.value === 'string') {
              return;
            }
            const splitS3Path = evidence.value.path?.split('.') ?? ['', ''];
            return {
              size: evidence.value.fileSize,
              dateAdded: evidence.created,
              status: evidence.value.scanStatus ?? null,
              fileS3Path: stripFileExtension(evidence.value.path ?? ''),
              fileExtension:
                evidence.value.fileExtension ?? splitS3Path.pop() ?? '',
              dataDeletedAt: evidence.value.dataDeletedAt,
            };
          })
      : [];
  };

  private getFormattedDate = (date: string) => {
    return isValidDate(date)
      ? formatDate(date, DateFormats.DD_MMM_YYYY, false).toLocaleUpperCase()
      : date;
  };

  private getReporterName = (publicReport: Types.PublicReport | null) => {
    const name = publicReport?.reporter?.name ?? undefined;
    const email = publicReport?.reporter?.email ?? undefined;
    const anonymous = publicReport?.anonymous ?? true;

    if (!anonymous && name !== undefined && email !== undefined) {
      return `${name}, ${email}`;
    }

    if (!anonymous && name === undefined && email !== undefined) {
      return email;
    }

    if (!anonymous && name !== undefined && email === undefined) {
      return name;
    }

    return I18next.t('public.PublicReport.anonymous');
  };

  private getCategoryName = (publicReport: Types.PublicReport | null) => {
    return (
      (
        publicReport?.reportedCategory as {
          name: string;
          id: number;
        }
      )?.name || 'No category chosen'
    );
  };

  public render() {
    if (this.state.isLoading)
      return (
        <Center>
          <Spinner variant="secondary" size="xlarge" />
        </Center>
      );

    const { publicReport } = this.state;

    const perpetratorNames = () => {
      if (publicReport) {
        if (publicReport.source === 'vault-talk') {
          const perpetrator = publicReport.evidence.evidence.find(
            (e) => e.type === '@evidence/perpetrator',
          )?.value as { name: string; external: boolean };
          return perpetrator?.name;
        }
        return `${publicReport.perpetrators
          .map((perpetrator) => {
            if (
              perpetrator.name?.includes('[deleted') ||
              perpetrator.dataDeletedAt
            ) {
              let deletedDate = 'UNKNOWN DATE';
              if (perpetrator.dataDeletedAt) {
                deletedDate = this.getFormattedDate(perpetrator.dataDeletedAt);
              } else if (perpetrator.name?.includes('[deleted')) {
                const match = perpetrator.name.match(/\[deleted\s+(.+?)\]/);
                if (match && match[1]) {
                  deletedDate = match[1];
                }
              }
              return `[Deleted ${deletedDate}]\n`;
            }
            return `${perpetrator.name}\n`;
          })
          .join('')}`;
      }
      return '';
    };

    const getStepRecording = (step: CallStep) => {
      const recording = publicReport?.audioRecordings?.find(
        (recording) =>
          recording.step.toLocaleLowerCase() === step.toLocaleLowerCase(),
      );
      if (!recording || recording?.dataDeletedAt) {
        return '';
      }
      return recording?.url ?? '';
    };
    const reportId = this.state.publicReport?.id || '';

    return (
      <PublicLoggedInContainerWithNavbar
        organisation={this.props.organisation}
        currentUser={this.props.currentUser}
        selectedTabIndex={0}
        hideMenusWhenSmall={false}
        hideID={false}
      >
        <BackLinkContainer data-testid={'back-to-reports'}>
          <BackTextLink
            to={`/${this.props.organisation}/open-reporting/reports`}
          >
            <KeyboardArrowLeft />
            {String(I18next.t('public.PublicReport.backToReports'))}
          </BackTextLink>
        </BackLinkContainer>

        <PublicReportCard
          reportId={reportId}
          linkToViewReport={false}
          organisation={this.props.organisation}
          category={this.getCategoryName(this.state.publicReport)}
          displayId={getReadableIdWithDash(reportId)}
          status={this.state.publicReport?.status || ''}
          created={
            isValidDate(this.state.publicReport?.created)
              ? toLocalTimezone(
                  this.state.publicReport?.created,
                  'DD/MMM/YYYY',
                ).toUpperCase()
              : ''
          }
          source={this.state.publicReport?.source || ''}
        />

        <EvidenceList data-testid={'evidence-list'}>
          <PublicReportEvidence
            evidenceTitle={I18next.t('public.Date.header')}
            evidenceContent={this.getFormattedDate(
              this.getEvidenceContent(EvidenceType.DATE),
            )}
            firstEvidence
            evidenceAudio={
              getStepRecording(CallStep.Date) && (
                <AudioRecording
                  audioType="audio/mpeg"
                  audioSource={getStepRecording(CallStep.Date)}
                  data-testid={'evidence-audio-recording-date'}
                />
              )
            }
          />
          <PublicReportEvidence
            evidenceTitle={I18next.t('public.Time.header')}
            evidenceContent={this.getEvidenceContent(EvidenceType.TIME)}
            evidenceAudio={
              getStepRecording(CallStep.Time) && (
                <AudioRecording
                  audioType="audio/mpeg"
                  audioSource={getStepRecording(CallStep.Time)}
                  data-testid={'evidence-audio-recording-time'}
                />
              )
            }
          />
          <PublicReportEvidence
            evidenceTitle={I18next.t('public.Location.header')}
            evidenceContent={this.getEvidenceContent(EvidenceType.LOCATION)}
            evidenceAudio={
              getStepRecording(CallStep.Location) && (
                <AudioRecording
                  audioType="audio/mpeg"
                  audioSource={getStepRecording(CallStep.Location)}
                  data-testid={'evidence-audio-recording-location'}
                />
              )
            }
          />

          <PublicReportEvidence
            evidenceTitle={I18next.t('public.Description.whatHappened')}
            evidenceContent={this.getEvidenceContent(EvidenceType.DESCRIPTION)}
            evidenceAudio={
              (getStepRecording(CallStep.Description) ||
                getStepRecording(CallStep.Question)) && (
                <AudioRecording
                  audioType="audio/mpeg"
                  audioSource={
                    getStepRecording(CallStep.Description) ||
                    getStepRecording(CallStep.Question)
                  }
                  data-testid={'evidence-audio-recording-description'}
                />
              )
            }
          />

          <PublicReportEvidence
            evidenceTitle={I18next.t('public.Relationship.header', {
              orgName: this.props.organisation,
            })}
            evidenceContent={this.state.publicReport?.relationshipType ?? ''}
          />
          {this.props.isOprCountryEnabled &&
            this.state.publicReport?.selectedCountryName && (
              <PublicReportEvidence
                evidenceTitle={I18next.t('public.Country.header')}
                evidenceContent={this.state.publicReport.selectedCountryName}
              />
            )}
          <PublicReportEvidence
            evidenceTitle={I18next.t('public.Emotion.header')}
            evidenceContent={this.getEvidenceContent(EvidenceType.EMOTION)}
            evidenceAudio={
              getStepRecording(CallStep.Emotion) && (
                <AudioRecording
                  audioType="audio/mpeg"
                  audioSource={getStepRecording(CallStep.Emotion)}
                  data-testid={'evidence-audio-recording-emotion'}
                />
              )
            }
          />
          <PublicReportEvidence
            evidenceTitle={I18next.t('public.Witnesses.header')}
            evidenceContent={this.getEvidenceContent(EvidenceType.WITNESS)}
            evidenceAudio={
              getStepRecording(CallStep.Witness) && (
                <AudioRecording
                  audioType="audio/mpeg"
                  audioSource={getStepRecording(CallStep.Witness)}
                  data-testid={'evidence-audio-recording-witness'}
                />
              )
            }
          />
          <PublicReportEvidence
            evidenceTitle={I18next.t('public.FileUpload.header')}
            evidenceContent={
              this.getFileUploadEvidenceContent() as UploadedEvidenceFile[]
            }
            reportId={this.props.reportId}
            isReporterViewEvidenceDisabled={
              this.props.isReporterViewEvidenceDisabled
            }
          />
          <PublicReportEvidence
            evidenceTitle={I18next.t('public.Perpetrators.header')}
            evidenceContent={perpetratorNames()}
            evidenceAudio={
              getStepRecording(CallStep.Perpetrator) && (
                <AudioRecording
                  audioType="audio/mpeg"
                  audioSource={getStepRecording(CallStep.Perpetrator)}
                  data-testid={'evidence-audio-recording-perpetrator'}
                />
              )
            }
          />
          <PublicReportEvidence
            evidenceTitle={I18next.t('public.Details.header')}
            evidenceContent={this.getReporterName(publicReport)}
            lastEvidence
            evidenceAudio={
              getStepRecording(CallStep.Name) && (
                <AudioRecording
                  audioType="audio/mpeg"
                  audioSource={getStepRecording(CallStep.Name)}
                  data-testid={'evidence-audio-recording-name'}
                />
              )
            }
          />
        </EvidenceList>
      </PublicLoggedInContainerWithNavbar>
    );
  }
}

const hideEvidenceContent = (props: { evidenceContent?: string }) => {
  return (
    !props.evidenceContent ||
    props.evidenceContent === 'undefined' ||
    props.evidenceContent === ''
  );
};

const PublicReportEvidence = (props: {
  evidenceTitle: string;
  evidenceContent: string | UploadedEvidenceFile[];
  firstEvidence?: boolean;
  lastEvidence?: boolean;
  evidenceAudio?: React.ReactNode;
  isReporterViewEvidenceDisabled?: boolean;
  reportId?: string;
}) => {
  const { t } = useTranslation();
  const isFileUploadQuestion = typeof props.evidenceContent !== 'string';
  const hasFilesUploaded = Boolean(
    isFileUploadQuestion &&
    props.evidenceContent &&
    props.evidenceContent.length > 0,
  );
  return (
    <Evidence firstEvidence={props.firstEvidence || false}>
      <EvidenceIcon firstEvidence={props.firstEvidence || false}>
        {!props.firstEvidence && <VerticalLine />}
        <img src={'/assets/padlock-icon.png'} alt={'Padlock Icon'} />
        {!props.lastEvidence && <VerticalLine />}
      </EvidenceIcon>

      <EvidenceCard
        data-testid={`evidence-timeline/${props.evidenceTitle}`}
        noPadding={isFileUploadQuestion && hasFilesUploaded}
      >
        {typeof props.evidenceContent === 'string' ? (
          <>
            <h2 data-testid="evidence-title">{props.evidenceTitle}</h2>
            <p data-testid="evidence-content">
              {hideEvidenceContent({
                evidenceContent: props.evidenceContent as string,
              }) ? (
                <i data-testid="skipped">{t('public.PublicReport.skipped')}</i>
              ) : (
                props.evidenceContent
              )}
            </p>
          </>
        ) : (
          <PublicReportFileUploadedEvidence
            evidenceContent={props.evidenceContent || []}
            evidenceTitle={props.evidenceTitle}
            isReporterViewEvidenceDisabled={
              props.isReporterViewEvidenceDisabled
            }
            reportId={props.reportId}
            data-testid={`evidence-timeline/file-upload-evidence`}
          />
        )}
        {props.evidenceAudio}
      </EvidenceCard>
    </Evidence>
  );
};

const PublicReportFileUploadedEvidence = (props: {
  evidenceContent: UploadedEvidenceFile[];
  evidenceTitle: string;
  isReporterViewEvidenceDisabled?: boolean;
  reportId?: string;
  'data-testid'?: string;
}) => {
  const { t } = useTranslation();
  return props.evidenceContent.length && props.reportId ? (
    <UploadedEvidence
      data-testid={props['data-testid']}
      files={props.evidenceContent || []}
      reportId={props.reportId}
      localisedText={{
        tableTitle: t('public.Evidence.tableTitle'),
        webErrorMessage: t('public.Evidence.webErrorMessage'),
        mobileErrorMessage: t('public.Evidence.mobileErrorMessage'),
        nameHeading: t('public.Evidence.nameHeading'),
        addedHeading: t('public.Evidence.addedHeading'),
        statusHeading: t('public.Evidence.statusHeading'),
        failedToOpenFileMessage: t('public.Evidence.failedToOpenFileMessage'),
        fileStatuses: {
          pendingUpload: t('public.Evidence.fileStatuses.pendingUpload'),
          passedUpload: t('public.Evidence.fileStatuses.passedUpload'),
          infectedUpload: t('public.Evidence.fileStatuses.infectedUpload'),
          readyForDownload: t('public.Evidence.fileStatuses.readyForDownload'),
        },
      }}
      downloadEvidenceDisabled={props.isReporterViewEvidenceDisabled}
    />
  ) : (
    <>
      <h2 data-testid="evidence-title">{props.evidenceTitle}</h2>
      <p data-testid="evidence-content">
        <i data-testid="skipped">{t('public.PublicReport.skipped')}</i>
      </p>
    </>
  );
};

export default PublicReport;
